/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : models/configuration/index.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Interface definitions for Configuration related data
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 12, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 12, 2020
 * Description   : Add Interface definitions for Configuration related data
 *
 *
 ******************************************************************************/

import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';

interface ITailNoDetails {
  configModuleSerialNo: string;
  acSerialNo: string;
  tailNo: string;
  status: ConfigStatusType;
}

export interface IConfigData {
  configId: string;
  soldTo: string;
  salesOrder: string;
  purchaseOrder: string;
  fileName: string;
  createdDate: string;
  platform: string;
  quantity: number;
  productNumber : string;
  tailNoDetails: ITailNoDetails[];
}

export interface IConfigState {
  completed: {
    selectedSoldTo: string;
    data: IConfigData[];
    loading: boolean;
  };
}

export enum ConfigStatusType {
  Completed = 'COMPLETED',
  Draft = 'DRAFT',
}

interface ICustomerCode {
  soldTo: string;
}

export interface IGetCompletedConfigArgs extends ICustomerCode {
  status: ConfigStatusType.Completed;
}

// Configure Models
export type ISoftwareFeature = {
  [key: string]: {
    value: string;
  };
};

export interface IAircraftDetails {
  configModuleSerialNo: string;
  tailNo: string;
  acSerialNo: string;
}

export interface IAircraftDetailsFormItem extends IAircraftDetails {
  id?: number;
  status?: string;
}

export interface IAircraftDetailsItem extends IAircraftDetails {
  id: number;
  status: string;
}

export interface ISoftwareFeaturesFormItem {
  id: number;
  fieldId: string;
  value: string;
  editable: boolean;
}

export interface ISoftwareFeaturesItem extends ISoftwareFeaturesFormItem {
  dataset: string[];
  inputType: 'radio' | 'dropdown';
  name: string;
}

export interface IConfigureScreenData {
  make: string;
  model: string;
  configId: number | null;
  stc: string;
  installMode: string;
  antenna: string;
  quantity: number;
  aircraftDetails: IAircraftDetailsItem[];
  softwareFeatures: ISoftwareFeaturesItem[];
  productNumber : string;
  productDescription : string;
  remainingQuantity : number;
  totalQuantity : number;
  optRemainingQuantity : number;
  optTotalQuantity : number;
}

export interface IConfigureFormInitialValues {
  configId: number | null;
  aircraftDetails: IAircraftDetailsFormItem[];
  softwareFeatures: ISoftwareFeaturesItem[];
}

export interface IAircraftFields {
  configModuleSerialNo: string | undefined;
  tailNo: string | undefined;
  acSerialNo: string | undefined;
}

export interface IYupValidationSchema {
  aircraftDetails: IAircraftFields[];
}

export interface IConfirmSTCInitialOptionValues {
  modelOption: IOption[];
  stcOption: IOption[];
  installModeOption: IOption[];
}

export enum EnumConfigureFlow {
  New = 'New',
  Edit = 'Edit',
  Add = 'Add',
}

export interface IConfigureScreenParams {
  salesOrder: string;
  configId: string;
  soldTo: string;
  productNumber: string;
}

export interface ISaveFinalConfigResponse {
  message?: string;
}

export interface ISaveFinalConfig {
  loader: boolean;
  response: ISaveFinalConfigResponse | null;
  error: boolean;
}

export interface IConfigureWorkFlowState {
  configureFlow: EnumConfigureFlow;
  configureScreenParams: IConfigureScreenParams;
  configureScreenData: IConfigureScreenData;
  downloadConfigLoader: boolean;
  configAfterSaveDetails: IConfigAfterSaveDetails;
  saveFinalConfig: ISaveFinalConfig;
}

export interface IConfigAfterSaveDetails {
  loader: boolean;
  details: ISaveConfigResponse | null;
  pythonError: boolean;
}

export interface IDownloadConfigFile {
  configId: string;
  soldTo: string;
}

export interface ISoftwareFeatureDetail {
  id: number;
  value: string;
  editable: boolean;
}

export interface IUserDetail {
  userName: string;
  accountName: string;
  honeywellAccountCode: string;
  addressLine1: string;
}

export interface ISaveConfigData {
  configId: number;
  aircraftDetails: IAircraftDetailsFormItem[];
  softwareFeatures: ISoftwareFeatureDetail[];
  productNumber  : string;
}

export interface ISaveConfigRequestParam extends ISaveConfigData {
  userDetails: IUserDetail;
}

export enum EnumSaveConfigType {
  draft = 'DRAFT',
  complete = 'COMPLETE',
}

export interface ISaveConfigResponse {
  status: string;
  sysGenFilename: string;
  configurationReferenceName: string;
}

export interface ISaveFinalConfigRequestParam {
  fileName: string;
}

export interface ISaveFinalConfigFields {
  configFileRefName: string | '';
  sysGenFileName: string | '';
}
